/**
 * Custom Login Page
 * 
 * Matching v3's design with custom styling.
 * Uses Amplify Auth for Cognito authentication.
 * 
 * Handles:
 * - Standard email/password sign in
 * - New password required flow (first-time login with temp password)
 * - TOTP MFA setup (first-time MFA enrollment with QR code)
 * - TOTP MFA verification (entering authenticator code)
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signIn, confirmSignIn, signInWithRedirect } from 'aws-amplify/auth';
import loginBgWebp from '../assets/images/login-bg.webp';
import loginBgPng from '../assets/images/login-bg.png';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';

// MFA flow states
type AuthStep = 'login' | 'newPassword' | 'totpSetup' | 'totpVerify';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isOktaLoading, setIsOktaLoading] = useState(false);
  
  // Auth flow state
  const [authStep, setAuthStep] = useState<AuthStep>('login');
  
  // TOTP MFA state
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [qrCodeUri, setQrCodeUri] = useState('');

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Legacy compat
  const requiresNewPassword = authStep === 'newPassword';

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsEmailLoading(true);

    try {
      const result = await signIn({ username, password });
      
      if (result.isSignedIn) {
        navigate(from, { replace: true });
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // User needs to set a new password (first login with temp password)
        setAuthStep('newPassword');
        setPassword(''); // Clear the temp password field
      } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        // User needs to set up TOTP MFA for the first time
        // The TOTP setup details are included in the sign-in response
        console.log('Setting up TOTP MFA...', result.nextStep);
        const totpSetupDetails = result.nextStep.totpSetupDetails;
        
        if (totpSetupDetails) {
          const secretKey = totpSetupDetails.sharedSecret;
          const appName = 'LookerEmbed';
          const qrUri = totpSetupDetails.getSetupUri(appName, username).toString();
          
          setTotpSecret(secretKey);
          setQrCodeUri(qrUri);
          setAuthStep('totpSetup');
        } else {
          console.error('TOTP setup details not found in response');
          setError('MFA setup failed. Please try again.');
        }
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        // User has TOTP set up, needs to enter code
        setAuthStep('totpVerify');
      } else {
        console.log('Unhandled sign-in step:', result.nextStep?.signInStep);
        setError('Sign in incomplete. Please try again.');
      }
    } catch (err) {
      console.error('Sign in error:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAuthorizedException') {
          setError('Invalid credentials. Please check your email and password.');
        } else if (err.name === 'UserNotFoundException') {
          setError('User not found. Please check your email address.');
        } else if (err.name === 'UserNotConfirmedException') {
          setError('Account not confirmed. Please contact your administrator.');
        } else {
          setError(err.message || 'Sign in failed. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Validate password length (Cognito default minimum is 8)
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setIsEmailLoading(true);

    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (result.isSignedIn) {
        navigate(from, { replace: true });
      } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        // After password change, user needs to set up MFA
        // The TOTP setup details are included in the confirmSignIn response
        console.log('Setting up TOTP MFA after password change...', result.nextStep);
        const totpSetupDetails = result.nextStep.totpSetupDetails;
        
        if (totpSetupDetails) {
          const secretKey = totpSetupDetails.sharedSecret;
          const appName = 'LookerEmbed';
          const qrUri = totpSetupDetails.getSetupUri(appName, username).toString();
          
          setTotpSecret(secretKey);
          setQrCodeUri(qrUri);
          setAuthStep('totpSetup');
        } else {
          console.error('TOTP setup details not found in response');
          setError('MFA setup failed. Please try again.');
        }
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        // User has TOTP set up, needs to enter code
        setAuthStep('totpVerify');
      } else {
        setError('Password change incomplete. Please try again.');
      }
    } catch (err) {
      console.error('New password error:', err);
      if (err instanceof Error) {
        if (err.name === 'InvalidPasswordException') {
          setError('Password does not meet requirements. Must include uppercase, lowercase, numbers, and special characters.');
        } else {
          setError(err.message || 'Failed to set new password. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsEmailLoading(false);
    }
  };

  /**
   * Handle TOTP setup - verifying the first code after scanning QR
   */
  const handleTotpSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (totpCode.length !== 6 || !/^\d+$/.test(totpCode)) {
      setError('Please enter a valid 6-digit code from your authenticator app.');
      return;
    }

    setIsEmailLoading(true);

    try {
      const result = await confirmSignIn({
        challengeResponse: totpCode,
      });

      if (result.isSignedIn) {
        navigate(from, { replace: true });
      } else {
        console.log('TOTP setup result:', result.nextStep);
        setError('MFA setup incomplete. Please try again.');
      }
    } catch (err) {
      console.error('TOTP setup error:', err);
      if (err instanceof Error) {
        if (err.name === 'CodeMismatchException' || err.message.includes('Code mismatch')) {
          setError('Invalid code. Please check your authenticator app and try again.');
        } else if (err.name === 'ExpiredCodeException') {
          setError('Code expired. Please enter the current code from your authenticator.');
        } else {
          setError(err.message || 'Failed to verify code. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsEmailLoading(false);
      setTotpCode('');
    }
  };

  /**
   * Handle TOTP verification - entering code for subsequent logins
   */
  const handleTotpVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (totpCode.length !== 6 || !/^\d+$/.test(totpCode)) {
      setError('Please enter a valid 6-digit code from your authenticator app.');
      return;
    }

    setIsEmailLoading(true);

    try {
      const result = await confirmSignIn({
        challengeResponse: totpCode,
      });

      if (result.isSignedIn) {
        navigate(from, { replace: true });
      } else {
        console.log('TOTP verify result:', result.nextStep);
        setError('Verification incomplete. Please try again.');
      }
    } catch (err) {
      console.error('TOTP verify error:', err);
      if (err instanceof Error) {
        if (err.name === 'CodeMismatchException' || err.message.includes('Code mismatch')) {
          setError('Invalid code. Please check your authenticator app and try again.');
        } else if (err.name === 'ExpiredCodeException') {
          setError('Code expired. Please enter the current code from your authenticator.');
        } else {
          setError(err.message || 'Failed to verify code. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsEmailLoading(false);
      setTotpCode('');
    }
  };

  /**
   * Handle Okta SAML sign-in - use Amplify's official API
   */
  const handleOktaSignIn = async () => {
    setError('');
    setIsOktaLoading(true);

    try {
      // Use Amplify's signInWithRedirect with the SAML provider name from defineAuth
      await signInWithRedirect({
        provider: { custom: 'rvoh-okta-saml' },
      });
    } catch (err) {
      console.error('Okta sign-in error:', err);
      if (err instanceof Error) {
        setError(err.message || 'Failed to initiate Okta sign-in. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsOktaLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundImage: `image-set(url(${loginBgWebp}) type("image/webp"), url(${loginBgPng}) type("image/png"))`,
        backgroundColor: '#2563eb',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'hidden',
        p: 4,
      }}
    >
      {/* Main container - side by side layout */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '5rem',
          maxWidth: 960,
          width: '100%',
          position: 'relative',
          zIndex: 1,
          // Responsive: stack on smaller screens
          '@media (max-width: 900px)': {
            flexDirection: 'column',
            gap: 4,
            textAlign: 'center',
          },
          '@media (max-width: 768px)': {
            '& > div:first-of-type': {
              display: 'none', // Hide branding on mobile
            },
          },
        }}
      >
        {/* Left branding panel */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: 420,
            '@media (max-width: 900px)': {
              maxWidth: 400,
            },
          }}
        >
          {/* Brand Logo */}
          <Box sx={{ mb: 4 }}>
            <img
              src="https://cdn.prod.website-files.com/656a0a9a0f8b818b1645dd46/656a1736026f636f54b04595_RVOH_logo_white.svg"
              alt="RVO Health"
              style={{ height: 48 }}
            />
          </Box>

          {/* Headline */}
          <Typography
            sx={{
              fontSize: '3rem',
              fontWeight: 700,
              color: 'white',
              lineHeight: 1.1,
              mb: 3,
              letterSpacing: '-0.04em',
              '@media (max-width: 900px)': {
                fontSize: '2.25rem',
              },
            }}
          >
            Looker{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #38bdf8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Analytics
            </Box>{' '}
            Dashboard Portal
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontSize: '1.125rem',
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: 1.6,
              mb: 5,
            }}
          >
            Securely access embedded Looker dashboards with real-time insights
            and interactive visualizations — all in one place.
          </Typography>

        </Box>

        {/* Right login form panel */}
        <Box
          sx={{
            flexShrink: 0,
            width: 400,
            '@media (max-width: 900px)': {
              width: '100%',
              maxWidth: 400,
            },
            '@media (max-width: 480px)': {
              '& > div': {
                p: 3,
                borderRadius: 3,
              },
            },
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.98)',
              borderRadius: 4,
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              p: 5,
              width: '100%',
              animation: 'slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards',
              '@keyframes slideUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(20px) scale(0.98)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0) scale(1)',
                },
              },
            }}
          >
            {authStep !== 'login' && (
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 0.5,
                    letterSpacing: '-0.03em',
                  }}
                >
                  {authStep === 'totpSetup' ? 'Set Up MFA' :
                   authStep === 'totpVerify' ? 'Two-Factor Auth' :
                   'Set New Password'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {authStep === 'totpSetup' ? 'Secure your account with an authenticator app' :
                   authStep === 'totpVerify' ? 'Enter code from your authenticator app' :
                   'Please create a new password to continue'}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  bgcolor: '#fef2f2',
                  color: '#ef4444',
                  border: '1px solid #fecaca',
                  animation: 'scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                  '@keyframes scaleIn': {
                    from: { opacity: 0, transform: 'scale(0.95)' },
                    to: { opacity: 1, transform: 'scale(1)' },
                  },
                }}
              >
                {error}
              </Alert>
            )}

            {authStep === 'totpSetup' ? (
              /* TOTP Setup Form - First time MFA enrollment */
              <form onSubmit={handleTotpSetupSubmit}>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
                </Typography>

                {/* QR Code */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'white',
                  }}
                >
                  {qrCodeUri && (
                    <Box sx={{ width: 200, height: 200, mb: 2 }}>
                      <QRCodeSVG
                        value={qrCodeUri}
                        size={200}
                        level="M"
                        aria-label="TOTP QR Code"
                      />
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Can't scan? Enter this code manually:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'grey.100',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      mt: 1,
                      wordBreak: 'break-all',
                      textAlign: 'center',
                      fontSize: '0.75rem',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {totpSecret}
                  </Typography>
                </Paper>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="label"
                    htmlFor="totpCode"
                    sx={{
                      display: 'block',
                      mb: 1,
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Verification Code
                  </Typography>
                  <TextField
                    id="totpCode"
                    fullWidth
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    inputProps={{
                      maxLength: 6,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.25rem' },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '& fieldset': { borderWidth: 2 },
                      },
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isEmailLoading || totpCode.length !== 6}
                  sx={{
                    py: 1.5,
                    borderRadius: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #18181b 0%, #1e2d3d 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #18181b 0%, #1e2d3d 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
                    },
                    '&:disabled': {
                      opacity: 0.7,
                    },
                  }}
                >
                  {isEmailLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Complete Setup'}
                </Button>
              </form>
            ) : authStep === 'totpVerify' ? (
              /* TOTP Verification Form - Subsequent logins */
              <form onSubmit={handleTotpVerifySubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="label"
                    htmlFor="totpVerifyCode"
                    sx={{
                      display: 'block',
                      mb: 1,
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Authentication Code
                  </Typography>
                  <TextField
                    id="totpVerifyCode"
                    fullWidth
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem', fontWeight: 600 },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '& fieldset': { borderWidth: 2 },
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    Open your authenticator app to view your code
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isEmailLoading || totpCode.length !== 6}
                  sx={{
                    py: 1.5,
                    borderRadius: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #18181b 0%, #1e2d3d 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #18181b 0%, #1e2d3d 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
                    },
                    '&:disabled': {
                      opacity: 0.7,
                    },
                  }}
                >
                  {isEmailLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setAuthStep('login');
                    setTotpCode('');
                    setError('');
                  }}
                  sx={{ mt: 2, color: 'text.secondary' }}
                >
                  ← Back to Sign In
                </Button>
              </form>
            ) : requiresNewPassword ? (
              /* New Password Form */
              <form onSubmit={handleNewPasswordSubmit}>
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    bgcolor: '#eff6ff',
                    color: '#3b82f6',
                    border: '1px solid #bfdbfe',
                  }}
                >
                  Your temporary password has expired. Please create a new password.
                </Alert>

                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    component="label"
                    htmlFor="newPassword"
                    sx={{
                      display: 'block',
                      mb: 1,
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    New Password
                  </Typography>
                  <TextField
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    fullWidth
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoFocus
                    autoComplete="new-password"
                    placeholder="Enter new password"
                    size="medium"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                            {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '& fieldset': { borderWidth: 2 },
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Must be at least 8 characters with uppercase, lowercase, numbers, and special characters
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="label"
                    htmlFor="confirmNewPassword"
                    sx={{
                      display: 'block',
                      mb: 1,
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Confirm New Password
                  </Typography>
                  <TextField
                    id="confirmNewPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    fullWidth
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Confirm new password"
                    size="medium"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '& fieldset': { borderWidth: 2 },
                      },
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isEmailLoading}
                  sx={{
                    py: 1.5,
                    borderRadius: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #18181b 0%, #1e2d3d 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                      opacity: 0,
                      transition: 'opacity 0.15s ease-in-out',
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #18181b 0%, #1e2d3d 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
                      '&::before': { opacity: 1 },
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    '&:disabled': {
                      opacity: 0.7,
                      transform: 'none',
                    },
                  }}
                >
                  {isEmailLoading ? <CircularProgress size={24} color="inherit" /> : 'Set Password & Sign In'}
                </Button>
              </form>
            ) : (
              /* Login Form */
              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    component="label"
                    htmlFor="username"
                    sx={{
                      display: 'block',
                      mb: 1,
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Email
                  </Typography>
                  <TextField
                    id="username"
                    fullWidth
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoFocus
                    autoComplete="username"
                    placeholder="Enter your email"
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '& fieldset': { borderWidth: 2 },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="label"
                    htmlFor="password"
                    sx={{
                      display: 'block',
                      mb: 1,
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    Password
                  </Typography>
                  <TextField
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    size="medium"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '& fieldset': { borderWidth: 2 },
                      },
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isEmailLoading || isOktaLoading}
                  sx={{
                    py: 1.5,
                    borderRadius: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    bgcolor: '#3b82f6',
                    '&:hover': {
                      bgcolor: '#2563eb',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(59, 130, 246, 0.35)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    '&:disabled': {
                      opacity: 0.7,
                      transform: 'none',
                    },
                  }}
                >
                  {isEmailLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>

                {/* Okta SSO Button */}
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={handleOktaSignIn}
                  disabled={isEmailLoading || isOktaLoading}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderWidth: 2,
                    borderColor: '#3b82f6',
                    color: '#3b82f6',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: '#2563eb',
                      bgcolor: 'rgba(59, 130, 246, 0.04)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                    },
                    '&:disabled': {
                      opacity: 0.7,
                      transform: 'none',
                    },
                  }}
                >
                  {isOktaLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In with Okta'}
                </Button>
              </form>
            )}

          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default LoginPage;
